import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../components/common/Footer';
import './DrivePage.css';

const PERKS = [
  { icon: '💰', title: 'Return trips filled', body: 'We match your empty return journey with a passenger. You earn more per day without driving more.' },
  { icon: '📅', title: 'Your schedule, your rules', body: 'Work when you want. No daily targets, no forced trips, no pressure. Full flexibility.' },
  { icon: '🏆', title: 'Weekly UPI payments', body: 'Every Sunday, earnings go straight to your bank. No delays, no deductions, no excuses.' },
  { icon: '📱', title: 'Simple WhatsApp booking', body: 'No complicated app to learn. Bookings come via WhatsApp. Confirm in one tap.' },
  { icon: '🔒', title: 'Verified customers only', body: "All customers are phone-verified. You always know who you're picking up." },
  { icon: '📈', title: 'Grow with the platform', body: 'As ZilaCab grows across Bihar, your booking volume grows too. Early partners earn the most.' },
];

const REQUIREMENTS = [
  'Valid driving licence (LMV or above)',
  'Vehicle RC book in your name or family',
  'Commercial vehicle insurance (active)',
  'Vehicle fitness certificate (valid)',
  'Aadhaar card + PAN card',
  'Bank account for weekly payments',
  'Smartphone with WhatsApp',
  'Vehicle: Sedan, SUV, Tempo Traveller, or Truck',
];

const PROCESS = [
  { num: 1, title: 'Fill registration form', body: 'Takes 5 minutes. Name, vehicle, city, documents.' },
  { num: 2, title: 'Document verification', body: 'Our team verifies your documents within 24 hours.' },
  { num: 3, title: 'Onboarding call', body: 'Quick call to explain how bookings work and answer questions.' },
  { num: 4, title: 'Start getting bookings', body: 'Go live on ZilaCab. First booking usually within 48 hours.' },
];

const PERKS_BULLETS = [
  'Zero entry fee — ever',
  'Weekly bank payments every Sunday',
  'You keep 85% of every fare',
  'Work across all 38 Bihar districts',
  'Return trips filled automatically',
];

const VEHICLE_OPTIONS = [
  'Sedan (Maruti, Honda, etc.)',
  'SUV (Innova, Ertiga, etc.)',
  'Tempo Traveller',
  'Mini Truck / Pickup',
  'Large Truck',
  'Auto Rickshaw',
];

const EXPERIENCE_OPTIONS = [
  'Less than 1 year',
  '1–3 years',
  '3–5 years',
  '5–10 years',
  '10+ years',
];

const DrivePage = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [vehicleType, setVehicleType] = useState(VEHICLE_OPTIONS[0]);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [city, setCity] = useState('');
  const [experience, setExperience] = useState(EXPERIENCE_OPTIONS[1]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name || !phone || !vehicleNumber || !city) {
      toast.error('Please fill all required fields');
      return;
    }
    toast.success('Application submitted! Our team will call within 24 hours.');
  };

  return (
    <div className="drive-page">
      <section className="drive-hero">
        <div className="drive-hero-left">
          <div className="section-eyebrow on-dark" style={{ color: 'var(--orange-light, #FF7B3A)' }}>Join our fleet</div>
          <h1>Drive with ZilaCab.<em>Earn more.</em></h1>
          <p>Own a cab, tempo, or truck? Join Bihar's fastest growing platform. No entry fee. Weekly payments. You choose your hours.</p>
          <div className="earnings-bar">
            <div className="eb-label">Average driver earns</div>
            <div className="eb-amount">₹35,000 – ₹55,000</div>
            <div className="eb-sub">per month · after fuel costs · 3 trips/day average</div>
          </div>
        </div>
        <div className="drive-hero-right">
          <div className="earn-calc">
            <div className="ec-title">Your earnings calculator — example</div>
            <div className="ec-row"><span className="ec-label">Trips per day</span><span className="ec-val">3</span></div>
            <div className="ec-row"><span className="ec-label">Working days/month</span><span className="ec-val">25</span></div>
            <div className="ec-row"><span className="ec-label">Avg fare per trip</span><span className="ec-val">₹1,200</span></div>
            <div className="ec-row"><span className="ec-label">Total monthly revenue</span><span className="ec-val">₹90,000</span></div>
            <div className="ec-row"><span className="ec-label">ZilaCab commission (15%)</span><span className="ec-val muted">−₹13,500</span></div>
            <div className="ec-row"><span className="ec-label">Fuel costs (est.)</span><span className="ec-val muted">−₹22,500</span></div>
            <div className="ec-row total"><span className="ec-label">Your net monthly earnings</span><span className="ec-val">₹54,000</span></div>
          </div>
        </div>
      </section>

      <section className="perks-section">
        <div className="perks-inner">
          <div className="section-eyebrow">Why ZilaCab</div>
          <h2 className="section-title">Better than going solo</h2>
          <p className="section-sub">ZilaCab fills your return trips, pays weekly, and treats drivers as partners — not contractors.</p>
          <div className="perks-grid">
            {PERKS.map((p) => (
              <div key={p.title} className="perk-card">
                <div className="perk-ic">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="requirements-section">
        <div className="req-inner-wrap">
          <div className="section-eyebrow">How to join</div>
          <h2 className="section-title">Requirements &amp; process</h2>
          <p className="section-sub">Simple requirements. Fast onboarding. Start earning within a week.</p>
          <div className="req-inner">
            <div className="req-col">
              <h3>What you need</h3>
              <ul className="req-list">
                {REQUIREMENTS.map((r) => <li key={r}>{r}</li>)}
              </ul>
            </div>
            <div className="req-col">
              <h3>Onboarding process</h3>
              <div className="process-steps">
                {PROCESS.map((p) => (
                  <div key={p.num} className="ps-item">
                    <div className="ps-num">{p.num}</div>
                    <div className="ps-text">
                      <h5>{p.title}</h5>
                      <p>{p.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="drive-form-section">
        <div className="drive-form-inner">
          <div>
            <div className="section-eyebrow">Register today</div>
            <h2 className="section-title">Join as a driver partner</h2>
            <p className="section-sub">No entry fee. No monthly charges. Just fill the form and we'll call you within 24 hours.</p>
            <div className="drive-perks-list">
              {PERKS_BULLETS.map((b) => (
                <div key={b} className="dpl-row"><span className="ck">✓</span> {b}</div>
              ))}
            </div>
          </div>
          <form className="drive-form-card" onSubmit={handleSubmit}>
            <h3>Driver registration</h3>
            <p>Fill this form and our team will call you within 24 hours.</p>
            <div className="form-grid-2">
              <div className="fg">
                <label>Full name</label>
                <input type="text" placeholder="Aapka pura naam" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="fg">
                <label>Phone number</label>
                <input type="tel" placeholder="+91 98000 00000" value={phone} onChange={(e) => setPhone(e.target.value)} required />
              </div>
              <div className="fg">
                <label>Vehicle type</label>
                <select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
                  {VEHICLE_OPTIONS.map((v) => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="fg">
                <label>Vehicle number</label>
                <input type="text" placeholder="BR01AB1234" value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())} required />
              </div>
              <div className="fg span2">
                <label>Base city / district</label>
                <input type="text" placeholder="e.g. Patna, Gaya, Muzaffarpur, Darbhanga" value={city} onChange={(e) => setCity(e.target.value)} required />
              </div>
              <div className="fg span2">
                <label>Years of driving experience</label>
                <select value={experience} onChange={(e) => setExperience(e.target.value)}>
                  {EXPERIENCE_OPTIONS.map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
              <div className="span2">
                <button type="submit" className="btn-or" style={{ width: '100%', padding: 14 }}>
                  Join as driver partner →
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DrivePage;
